import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Stethoscope, AlertTriangle, ChevronDown, ChevronRight, RefreshCw, GraduationCap } from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/kaizen";

const SEV = {
  high: { label: "Élevé", cls: "bg-[#D90429]/20 text-[#D90429] border-[#D90429]/40" },
  medium: { label: "Moyen", cls: "bg-amber-500/15 text-amber-400 border-amber-500/40" },
  low: { label: "Faible", cls: "bg-white/10 text-white/60 border-white/20" },
};

export default function AnomaliesModule() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/anomalies/audit", { timeout: 60000 });
      setData(data);
    } catch {
      toast.error("Impossible de charger l'audit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  return (
    <div className="max-w-5xl mx-auto" data-testid="anomalies-module">
      <PageHeader
        icon={Stethoscope}
        title="Centre d'anomalies"
        subtitle="Vue LECTURE SEULE de tout ce qui cloche : ruptures Stripe↔contrats, échéances manquantes, membres sans carnet… Aucune modification n'est faite ici."
        actions={
          <button data-testid="anomalies-refresh" onClick={load} disabled={loading}
            className="border border-white/15 hover:border-[#D90429] px-4 py-2.5 flex items-center gap-2 text-sm transition-colors disabled:opacity-50">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Actualiser
          </button>
        }
      />

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="anomalies-totaux">
          {[
            { l: "Contrats totaux", v: data.totals.contrats_total },
            { l: "Contrats actifs", v: data.totals.contrats_actifs },
            { l: "Anomalies détectées", v: data.totals.anomalies_total, accent: true },
            { l: "Catégories", v: data.totals.categories },
          ].map((s) => (
            <div key={s.l} className={`border p-4 ${s.accent ? "border-[#D90429]/50 bg-[#D90429]/10" : "border-white/10 bg-black/30"}`}>
              <p className="text-xs uppercase tracking-wider text-white/50">{s.l}</p>
              <p className={`text-3xl font-semibold mt-1 ${s.accent ? "text-[#D90429]" : "text-white"}`}>{s.v}</p>
            </div>
          ))}
        </div>
      )}

      <CarnetAuto />

      <SectionCard title="Anomalies par catégorie">
        {loading && !data ? (
          <p className="text-white/50 text-sm">Analyse en cours…</p>
        ) : !data ? null : (
          <div className="space-y-3" data-testid="anomalies-list">
            {data.categories.map((cat) => {
              const sev = SEV[cat.severity] || SEV.low;
              const isOpen = open[cat.key];
              return (
                <div key={cat.key} className="border border-white/10 bg-black/30" data-testid={`anomalie-${cat.key}`}>
                  <button onClick={() => toggle(cat.key)}
                    className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      {isOpen ? <ChevronDown size={18} className="text-white/50 shrink-0" /> : <ChevronRight size={18} className="text-white/50 shrink-0" />}
                      <AlertTriangle size={16} className={cat.count > 0 ? "text-[#D90429] shrink-0" : "text-emerald-400 shrink-0"} />
                      <span className="font-medium text-white truncate">{cat.label}</span>
                      <span className={`text-[10px] uppercase tracking-wider border px-2 py-0.5 shrink-0 ${sev.cls}`}>{sev.label}</span>
                    </div>
                    <span className={`text-2xl font-semibold shrink-0 ${cat.count > 0 ? "text-[#D90429]" : "text-emerald-400"}`} data-testid={`anomalie-count-${cat.key}`}>
                      {cat.count}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-white/10 pt-3">
                      <p className="text-xs text-white/50 mb-3">{cat.hint}</p>
                      {cat.count === 0 ? (
                        <p className="text-sm text-emerald-400">✓ Aucune anomalie dans cette catégorie.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {cat.sample.map((s, i) => (
                            <div key={i} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-1.5">
                              <span className="text-white/80 truncate">{s.nom}</span>
                              <span className="text-white/40 text-xs truncate text-right">{s.detail}</span>
                            </div>
                          ))}
                          {cat.count > cat.sample.length && (
                            <p className="text-xs text-white/40 pt-1">… et {cat.count - cat.sample.length} autre(s).</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function CarnetAuto() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get("/carnet-auto/preview"); setPreview(data); }
    catch { toast.error("Prévisualisation impossible"); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    if (!preview?.count) return;
    if (!window.confirm(`Créer ${preview.count} carnet(s) manquant(s) (ceinture Blanche) ?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/carnet-auto/apply?confirm=true");
      toast.success(`✅ ${data.created} carnet(s) créé(s)`);
      run();
    } catch { toast.error("Création impossible"); }
    finally { setBusy(false); }
  };
  return (
    <div id="carnet-auto" className="mb-6 border border-white/10 bg-black/30 p-4" data-testid="carnet-auto-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <GraduationCap size={18} className="text-[#D90429]" />
          <div>
            <p className="text-sm text-white/80 font-medium">Carnets auto par discipline (Phase 3)</p>
            <p className="text-xs text-white/50">Crée les carnets manquants pour les membres actifs (BJJ, Can Ryu, Kickboxing/Muay thaï). Ceinture Blanche, sans doublon.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={run} disabled={busy} data-testid="carnet-auto-preview" className="border border-white/15 hover:border-[#D90429] px-3 py-2 text-sm transition-colors disabled:opacity-50">Vérifier</button>
          {preview && preview.count > 0 && (
            <button onClick={apply} disabled={busy} data-testid="carnet-auto-apply" className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-sm disabled:opacity-50">Créer {preview.count} carnet(s)</button>
          )}
        </div>
      </div>
      {preview && (
        <p className="text-xs mt-2 text-white/60" data-testid="carnet-auto-result">
          {preview.count === 0 ? "✓ Tous les membres actifs (disciplines pédagogiques) ont un carnet." : `${preview.count} membre(s) actif(s) sans carnet dans leur discipline.`}
        </p>
      )}
    </div>
  );
}
